import { z } from 'zod';

/**
 * Primitives every other schema is built from.
 *
 * The point of putting them here is that "what a slug is" gets decided once. When the
 * same rule is re-typed per resource they drift, and the drift only shows up as a
 * public URL that 404s because one screen let through a slug another cannot look up.
 */

/** A database surrogate key. Positive, integral, and accepted from a string path param. */
export const idSchema = z.coerce.number().int().positive();

/**
 * URL slug. Lowercase, digits, single hyphens, no leading or trailing hyphen.
 *
 * Reserved words are refused because a project at `/projects/new` would be
 * unreachable — the back office owns that path — and the failure would look like a
 * routing bug rather than a naming one.
 */
const RESERVED_SLUGS = new Set(['new', 'edit', 'admin', 'api', 'null', 'undefined']);

export const slugSchema = z
  .string()
  .trim()
  .min(1, 'A slug is required')
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and single hyphens')
  .refine((s) => !RESERVED_SLUGS.has(s), { message: 'That word is reserved' });

/** Build a slug from a title. Same algorithm the back office previews as you type. */
export function slugify(input: string): string {
  return input
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

/** Money on the wire: a decimal string, or a number the client was lazy about. */
export const moneySchema = z
  .union([z.string(), z.number()])
  .refine(
    (v) => (typeof v === 'number' ? Number.isFinite(v) : /^-?\d+(\.\d+)?$/.test(v.trim())),
    { message: 'Enter an amount like 58500.00' },
  )
  .transform((v) => (typeof v === 'number' ? v.toFixed(2) : v.trim()));

/** A non-negative price. Refuses the negative that `moneySchema` alone would allow. */
export const priceSchema = moneySchema.refine((v) => !v.startsWith('-'), {
  message: 'A price cannot be negative',
});

export const emailSchema = z.string().trim().toLowerCase().email('Enter a valid email address').max(200);

/**
 * Phone number, stored exactly as typed.
 *
 * Deliberately permissive: Sri Lankan numbers are written `011 330 40 48`,
 * `+94 11 330 40 48` and `0113304048` by different people, all of them correct, and a
 * regex strict enough to normalise them rejects the diaspora numbers this business
 * also quotes to. It is a string a human will dial, not a key.
 */
export const phoneSchema = z
  .string()
  .trim()
  .min(6, 'That looks too short for a phone number')
  .max(40)
  .regex(/^[+()\-\s\d./]+$/, 'Use digits, spaces and + ( ) - only');

/** An absolute URL, or a site-relative path starting with `/`. */
export const linkSchema = z
  .string()
  .trim()
  .max(500)
  .refine((v) => /^https?:\/\//i.test(v) || v.startsWith('/') || v.startsWith('#') || /^mailto:|^tel:/i.test(v), {
    message: 'Use a full URL, or a path beginning with /',
  });

/** Optional free text that treats an empty box as "not set" rather than as "". */
export const optionalText = (max = 2000) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .nullable()
    .transform((v) => (v === '' || v === undefined ? null : v));

export const requiredText = (max = 200, label = 'This field') =>
  z.string().trim().min(1, `${label} is required`).max(max);

/** `sort_order` — the hand-set position of a row within its list. */
export const sortOrderSchema = z.coerce.number().int().min(0).max(100_000).default(0);

/* -------------------------------------------------------------------------- */
/* Listing                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Pagination accepted by every list endpoint.
 *
 * `pageSize` is capped at 200. Without a ceiling the first person to pass
 * `?pageSize=100000` pulls the whole products table with its images through one
 * request, and it will be a crawler rather than a person.
 */
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
});
export type Pagination = z.infer<typeof paginationSchema>;

export const sortDirectionSchema = z.enum(['asc', 'desc']).default('desc');

/** The envelope every list endpoint returns inside `data`. */
export interface Paginated<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/* -------------------------------------------------------------------------- */
/* SEO                                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Per-record SEO overrides. All optional — when a field is null the renderer falls
 * back to the record's own title and summary, so nothing needs filling in to be
 * correct, and filling one in wins.
 */
export const seoSchema = z.object({
  seoTitle: optionalText(70),
  seoDescription: optionalText(180),
  ogImageUrl: optionalText(500),
});
export type SeoInput = z.infer<typeof seoSchema>;
